import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, X, RefreshCw, CreditCard } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import { assetFinanceService } from '../../services/assetFinanceService';
import { assetService } from '../../services/assetService';

const CreateLoan = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [assets, setAssets] = useState([]);

  const [loanFormData, setLoanFormData] = useState({
    assetId: '',
    loanAmount: '',
    interestRate: '',
    loanTerm: '',
    lender: '',
    startDate: '',
    loanType: '',
    collateralType: '',
  });

  const extractAssetList = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.assets)) return payload.assets;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.data)) return payload.data;
    return [];
  };

  useEffect(() => {
    const loadAssets = async () => {
      try {
        const response = await assetService.getAssets({ limit: 100, page: 1 });
        const payload = response?.data?.data || response?.data || response;
        setAssets(extractAssetList(payload));
      } catch (err) {
        showError(err.message || 'Failed to load assets');
      }
    };
    loadAssets();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    try {
      // Validate assetId is a valid Mongo ObjectId
      const objectIdRegex = /^[0-9a-fA-F]{24}$/;
      if (!loanFormData.assetId || !objectIdRegex.test(loanFormData.assetId)) {
        setIsProcessing(false);
        showError('Please select a valid asset before saving the loan');
        return;
      }

      const payload = {
        asset: loanFormData.assetId,
        loanAmount: Number(loanFormData.loanAmount),
        interestRate: Number(loanFormData.interestRate),
        loanTerm: Number(loanFormData.loanTerm),
        lender: loanFormData.lender,
        startDate: loanFormData.startDate,
        loanType: loanFormData.loanType,
        collateralType: loanFormData.collateralType,
      };

      await assetFinanceService.createAssetLoan(payload);
      success('Asset loan created successfully');
      navigate('/finance/assets');
    } catch (error) {
      showError(error.message || 'Failed to save asset loan');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    navigate('/finance/assets');
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
    backgroundColor: 'white',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
    fontFamily: 'var(--font-family)',
  };

  const headerActions = [
    {
      label: 'Cancel',
      icon: <X size={18} />,
      onClick: handleCancel,
      variant: 'outline',
      tooltip: 'Cancel and go back',
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="New Asset Loan"
        description="Create a new loan record for an asset with financing details"
        actions={headerActions}
      />

      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        <div style={{ width: '100%', margin: '0 auto' }}>
          <form onSubmit={handleSubmit}>
            {/* Loan Details Card */}
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                marginBottom: '24px',
                overflow: 'hidden',
              }}
            >
              {/* Card Header */}
              <div
                style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <CreditCard size={18} style={{ color: 'var(--primary-color)' }} />
                <h3
                  style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111827',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  Loan Information
                </h3>
              </div>

              {/* Card Body */}
              <div style={{ padding: '24px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '20px',
                  }}
                >
                  {/* Asset */}
                  <div>
                    <label style={labelStyle}>Asset *</label>
                    <select
                      value={loanFormData.assetId}
                      onChange={(e) =>
                        setLoanFormData({ ...loanFormData, assetId: e.target.value })
                      }
                      required
                      style={inputStyle}
                    >
                      <option value="">-- Select asset --</option>
                      {assets && assets.length > 0 ? (
                        assets.map((a) => (
                          <option key={a._id || a.id} value={a._id || a.id}>
                            {a.name || a.assetCode || a._id || a.id}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          No assets loaded
                        </option>
                      )}
                    </select>
                  </div>

                  {/* Lender */}
                  <div>
                    <label style={labelStyle}>Lender *</label>
                    <input
                      type="text"
                      value={loanFormData.lender}
                      onChange={(e) =>
                        setLoanFormData({ ...loanFormData, lender: e.target.value })
                      }
                      required
                      placeholder="e.g., Bank of America"
                      style={inputStyle}
                    />
                  </div>

                  {/* Loan Amount */}
                  <div>
                    <label style={labelStyle}>Loan Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={loanFormData.loanAmount}
                      onChange={(e) =>
                        setLoanFormData({ ...loanFormData, loanAmount: e.target.value })
                      }
                      required
                      placeholder="0.00"
                      style={inputStyle}
                    />
                  </div>

                  {/* Interest Rate */}
                  <div>
                    <label style={labelStyle}>Interest Rate (%) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={loanFormData.interestRate}
                      onChange={(e) =>
                        setLoanFormData({ ...loanFormData, interestRate: e.target.value })
                      }
                      required
                      placeholder="e.g., 5.5"
                      style={inputStyle}
                    />
                  </div>

                  {/* Loan Term */}
                  <div>
                    <label style={labelStyle}>Loan Term (Months) *</label>
                    <input
                      type="number"
                      min="1"
                      value={loanFormData.loanTerm}
                      onChange={(e) =>
                        setLoanFormData({ ...loanFormData, loanTerm: e.target.value })
                      }
                      required
                      placeholder="e.g., 60"
                      style={inputStyle}
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label style={labelStyle}>Start Date *</label>
                    <input
                      type="date"
                      value={loanFormData.startDate}
                      onChange={(e) =>
                        setLoanFormData({ ...loanFormData, startDate: e.target.value })
                      }
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Loan Type */}
                  <div>
                    <label style={labelStyle}>Loan Type</label>
                    <input
                      type="text"
                      value={loanFormData.loanType}
                      onChange={(e) =>
                        setLoanFormData({ ...loanFormData, loanType: e.target.value })
                      }
                      placeholder="e.g., Term Loan, Lease, Mortgage"
                      style={inputStyle}
                    />
                  </div>

                  {/* Collateral Type */}
                  <div>
                    <label style={labelStyle}>Collateral Type</label>
                    <input
                      type="text"
                      value={loanFormData.collateralType}
                      onChange={(e) =>
                        setLoanFormData({ ...loanFormData, collateralType: e.target.value })
                      }
                      placeholder="e.g., Equipment, Property"
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions - Now inside the card */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  padding: '20px 24px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)',
                    color: 'white',
                    backgroundColor: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Create Loan
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateLoan;
